import { destinationPagePublicPath } from '@utazzo/shared';
import { UzButton, UzFormField, UzInput } from '@utazzo/ui';
import { Sparkles } from 'lucide-react';
import { useMemo } from 'react';
import { useFieldArray, useWatch, type UseFormReturn } from 'react-hook-form';
import { toast } from 'sonner';

import { AddRowButton, RowShell } from './RowShell';

import type { CreateWebsiteDestinationPageRequest } from '@utazzo/shared';

import { useCategoryPagesList } from '@/features/category-pages/use-category-pages';

export const SeoLinksEditor = ({
  form,
  disabled,
}: {
  form: UseFormReturn<CreateWebsiteDestinationPageRequest>;
  disabled?: boolean;
}) => {
  const { fields, append, remove, move } = useFieldArray({
    control: form.control,
    name: 'seoLinks',
  });
  const slug = useWatch({ control: form.control, name: 'slug' });
  const categoryPageIds = useWatch({ control: form.control, name: 'categoryPageIds' }) ?? [];
  const sliders = useWatch({ control: form.control, name: 'sliders' }) ?? [];

  const categoriesQuery = useCategoryPagesList({
    page: 1,
    pageSize: 200,
    sortBy: 'title',
    sortDir: 'asc',
  });
  const primaryCategorySlug = useMemo(() => {
    const firstId = categoryPageIds[0];
    if (!firstId) return null;
    return categoriesQuery.data?.data.find((p) => p.id === firstId)?.slug ?? null;
  }, [categoryPageIds, categoriesQuery.data]);

  const generateFromSliders = (): void => {
    const pageSlug = slug?.trim();
    if (!pageSlug) {
      toast.error('Set a page slug on the Details tab first.');
      return;
    }
    if (!primaryCategorySlug) {
      toast.error('Select at least one category page on the Details tab first.');
      return;
    }
    const basePath = destinationPagePublicPath(primaryCategorySlug, pageSlug);
    const groupLabel = 'Collections';
    const existingHrefs = new Set((form.getValues('seoLinks') ?? []).map((l) => l.href));
    let added = 0;
    for (const s of sliders) {
      if (!s.marketingTagId || !s.heading?.trim()) continue;
      const href = `${basePath}?collections=${s.marketingTagId}`;
      if (existingHrefs.has(href)) continue;
      append({ groupLabel, label: s.heading.trim(), href });
      existingHrefs.add(href);
      added += 1;
    }
    if (added === 0) {
      toast.message('No new collection links to add. Configure sliders on the Sliders tab.');
    } else {
      toast.success(`Added ${added} SEO link${added === 1 ? '' : 's'} from sliders.`);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-zinc-500">
        Internal-link clusters. Use the generator to link each collection slider to its filtered
        view on this page.
      </p>
      <UzButton
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled || sliders.length === 0}
        onClick={generateFromSliders}
      >
        <Sparkles className="mr-1.5 h-3.5 w-3.5" />
        Generate from sliders
      </UzButton>
      {fields.map((f, i) => (
        <RowShell
          key={f.id}
          index={i}
          total={fields.length}
          disabled={disabled}
          onMove={move}
          onRemove={remove}
        >
          <UzFormField name={`seoLinks.${i}.groupLabel` as const}>
            {({ field, error }) => (
              <UzInput
                {...field}
                value={(field.value as string | null) ?? ''}
                label="Group label"
                error={error}
                disabled={disabled}
              />
            )}
          </UzFormField>
          <UzFormField name={`seoLinks.${i}.label` as const}>
            {({ field, error }) => (
              <UzInput
                {...field}
                value={(field.value as string | null) ?? ''}
                label="Link label"
                error={error}
                disabled={disabled}
              />
            )}
          </UzFormField>
          <UzFormField name={`seoLinks.${i}.href` as const}>
            {({ field, error }) => (
              <UzInput
                {...field}
                value={(field.value as string | null) ?? ''}
                label="Link URL"
                placeholder="/international-tour-packages/bali?collections=<collection-id>"
                error={error}
                disabled={disabled}
              />
            )}
          </UzFormField>
        </RowShell>
      ))}
      <AddRowButton
        label="Add link"
        disabled={disabled}
        onClick={() => append({ groupLabel: '', label: '', href: '' })}
      />
    </div>
  );
};
